import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, getStatusColor, getBatteryColor, formatVoltage, getConnectivityColor } from "../HotelPage/utils/hotelUtils";
import { formatStayDate, getStayStatusLabel, getStayStatusStyle, resolveGreenMilesBaseUrl, formatQRDate } from "../GreenMiles/utils/greenMilesUtils";
import { ROOMS_LIMIT_OPTIONS } from "./hooks/useUnifiedHotelRooms";
import { ClickableStatus } from "../HotelPage/StatusChange";
import ClickableRoomTag, { getRoomTagBadgeClass } from "./ClickableRoomTag";
import { EditRoomModal } from "../Room/EditRoomModal";
import axios from "axios";
import { useAuth } from "react-oidc-context";

const CHIRPSTACK_BASE = "https://lora.luniwave.com/#/tenants/52f14cd4-c6f1-4fbd-8f87-4025e1d49242/applications";

// Désactivé : le status LS n'est pas utilisé pour l'instant
// const getLsStatusStyle = (status) => {
//   const baseStyle = { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 10px", borderRadius: "12px", fontSize: "0.75rem", fontWeight: 500 };
//   if (!status || status === "Aucun") return { ...baseStyle, backgroundColor: "#FFFFFF", border: "1px solid #111827", color: "#111827" };
//   if (status === "Action Ready") return { ...baseStyle, backgroundColor: "#C8FFC9", border: "1px solid #00D50E", color: "#0B3D0F" };
//   if (status === "Module Non-ready") return { ...baseStyle, backgroundColor: "#F8E5A6", border: "1px solid #D5AA00", color: "#3D2B00" };
//   return { ...baseStyle, backgroundColor: "#FFFFFF", border: "1px solid #111827", color: "#111827" };
// };

const isHeartbeatRecent = (lastHeartbeatDate) => {
  if (!lastHeartbeatDate) return false;
  try {
    const heartbeatTime = new Date(lastHeartbeatDate).getTime();
    const now = Date.now();
    const hoursSinceHeartbeat = (now - heartbeatTime) / (1000 * 60 * 60);
    return hoursSinceHeartbeat < 24;
  } catch {
    return false;
  }
};

const formatHeartbeatDate = (lastHeartbeatDate) => {
  if (!lastHeartbeatDate) return null;
  try {
    const date = new Date(lastHeartbeatDate);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes < 1 ? "À l'instant" : `Il y a ${diffMinutes} min`;
    }
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return formatDate(lastHeartbeatDate);
  } catch {
    return null;
  }
};

function GlobalRoomsTable({ rooms = [], total_count = 0, page = 1, total_pages = 1, limit = 30, onPageChange, onLimitChange, app_id, manager_id, masterId, has_lunishower, has_greenmiles, onEdit, onDelete, loading, onRefetch }) {
  const navigate = useNavigate();
  const [menuRoomId, setMenuRoomId] = useState(null);
  const menuRef = useRef(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  // État local pour les rooms afin de pouvoir les mettre à jour sans refetch
  const [localRooms, setLocalRooms] = useState(rooms);
  const auth = useAuth();
  const token = auth.user?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  // Synchroniser l'état local avec les props quand les rooms changent
  useEffect(() => {
    setLocalRooms(rooms);
  }, [rooms]);

  useEffect(() => {
    if (!menuRoomId) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuRoomId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuRoomId]);

  const openChirpstack = (room) => {
    if (room.devEUI && app_id) {
      window.open(`${CHIRPSTACK_BASE}/${app_id}/devices/${room.devEUI}`, "_blank");
    } else {
      alert("Aucun module lié ou app_id manquant.");
    }
  };

  const goEdit = (room) => {
    setMenuRoomId(null);
    if (onEdit) {
      onEdit(room);
      return;
    }
    // Ouvrir la modal simplifiée pour modifier la chambre
    setSelectedRoom(room);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRoom(null);
  };

  const handleRoomUpdated = () => {
    if (onRefetch) {
      onRefetch();
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setRoomToDelete(null);
  };

  const handleConfirmDeleteRoom = async () => {
    if (!roomToDelete || !roomToDelete.manager_room_id || !manager_id) {
      handleCloseDeleteModal();
      return;
    }
    try {
      await axios.delete(
        `${API_URL}/hotels/${manager_id}/rooms/${roomToDelete.manager_room_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onRefetch) onRefetch();
      handleCloseDeleteModal();
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
      alert(err.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const handleStatusChange = async (roomId, newStatus) => {
    if (!manager_id) {
      console.error("Manager ID manquant pour changer le statut");
      return;
    }

    // Mise à jour optimiste de l'état local (réactif immédiatement)
    setLocalRooms((prevRooms) =>
      prevRooms.map((room) => {
        // Vérifier si c'est la bonne chambre (peut être manager_room_id ou rId selon le contexte)
        if (room.manager_room_id === roomId || room.rId === roomId) {
          return { ...room, status: newStatus };
        }
        return room;
      })
    );

    try {
      await axios.patch(
        `${API_URL}/hotels/${manager_id}/rooms/${roomId}`,
        { module_status: newStatus, event_source: "USER_ACTION" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (err) {
      // Dans certains cas, le backend renvoie une erreur alors que la mise à jour SQL est déjà appliquée.
      // On log l'erreur en console sans interrompre l'expérience utilisateur.
      console.error("Erreur (non bloquante) lors du changement de statut:", err);
    }
  };

  const handleTagChange = async (rId, newTag) => {
    if (!masterId) {
      console.error("Master ID manquant pour changer le type de chambre");
      return;
    }

    // Mise à jour optimiste de l'état local (réactif immédiatement)
    setLocalRooms((prevRooms) =>
      prevRooms.map((room) => (room.rId === rId ? { ...room, rTag: newTag } : room))
    );

    try {
      await axios.patch(
        `${API_URL}/greenmiles/hotels/${masterId}/rooms/${rId}/tag`,
        { rTag: newTag },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (err) {
      console.error("Erreur lors du changement de type de chambre:", err);
      // Revenir à l'état serveur en cas d'échec
      if (onRefetch) onRefetch();
    }
  };

  const goDelete = (room) => {
    setMenuRoomId(null);
    if (onDelete) {
      onDelete(room);
      return;
    }
    if (room.manager_room_id && manager_id) {
      setRoomToDelete(room);
      setIsDeleteModalOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-8 text-center">
        <div className="text-gray-500 text-lg">Chargement des chambres...</div>
      </div>
    );
  }

  const hasPagination = total_count > 0 && (onPageChange || onLimitChange);
  const startItem = total_count === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total_count);

  if (!loading && rooms.length === 0 && total_count === 0) {
    return (
      <div className="bg-white shadow-md rounded-lg p-8 text-center">
        <div className="text-gray-500 text-lg">Aucune chambre pour cet hôtel.</div>
      </div>
    );
  }

  const paginationBar = hasPagination ? (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-white shadow rounded-lg px-4 py-3 border border-gray-200">
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-600">{total_count === 0 ? "0 chambre" : `${startItem}–${endItem} sur ${total_count} chambre${total_count > 1 ? "s" : ""}`}</span>
        {onLimitChange && (
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span>Afficher</span>
            <select value={limit} onChange={(e) => onLimitChange(Number(e.target.value))} className="rounded border border-gray-300 px-2 py-1 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              {ROOMS_LIMIT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {onPageChange && total_pages > 1 && (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="px-3 py-1.5 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            Précédent
          </button>
          <span className="text-sm text-gray-600 px-2">
            Page {page} / {total_pages}
          </span>
          <button type="button" onClick={() => onPageChange(page + 1)} disabled={page >= total_pages} className="px-3 py-1.5 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            Suivant
          </button>
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-3">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chambre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut LuniShower</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dernier changement</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tab connect</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batterie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type chambre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">QR code</th>
                {/* Colonne Statut LS désactivée - non utilisée pour l'instant */}
                {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut LS</th> */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LuniButton</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dernier séjour</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {localRooms.map((room) => (
                <tr key={room.rId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{room.rRoomNumber}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {room.status != null && has_lunishower && manager_id ? (
                      <ClickableStatus
                        room={{
                          id: room.manager_room_id || room.rId,
                          room_nb: room.rRoomNumber,
                          status: room.status,
                          devEUI: room.devEUI,
                          module_id: room.module_id,
                        }}
                        onStatusChange={(roomId, newStatus) => handleStatusChange(roomId, newStatus)}
                      />
                    ) : room.status != null ? (
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(room.status)}`}>{room.status}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{room.last_status_change ? formatDate(room.last_status_change) : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {room.rTag !== "Expérience" ? (
                      // Tab connect non applicable hors chambres Expérience
                      <span className="text-gray-400" title="Non applicable (chambre non Expérience)">—</span>
                    ) : room.tab_connect != null ? (
                      <div className="flex flex-col items-start gap-1">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getConnectivityColor(room.tab_connect?.status ?? room.tab_connect)}`}>{room.tab_connect?.status ?? room.tab_connect}</span>
                        {room.tab_connect?.last_update && <span className="text-xs text-gray-500">{formatDate(room.tab_connect.last_update)}</span>}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {room.battery?.voltage != null ? (
                      <div className="flex flex-col">
                        <span className={getBatteryColor(room.battery.voltage)}>{formatVoltage(room.battery.voltage)}</span>
                        {room.battery?.last_update && <span className="text-xs text-gray-500">{formatDate(room.battery.last_update)}</span>}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {masterId ? (
                      <ClickableRoomTag room={room} onTagChange={handleTagChange} />
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoomTagBadgeClass(room.rTag)}`}>
                        {room.rTag || "Aucun"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {room.has_qr_code === 1 && room.qrId ? (
                      <div className="flex items-center">
                        <div className="bg-green-50 border border-green-200 rounded-md p-2 max-w-xs">
                          <div className="text-xs font-mono text-green-800 truncate" title={room.qrId}>
                            {room.qrId}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const base = resolveGreenMilesBaseUrl();
                            navigator.clipboard.writeText(`${base}/${room.qrId}`);
                          }}
                          className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copier l'URL GreenMiles"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm italic">Aucun QR code</span>
                    )}
                  </td>
                  {/* Colonne Statut LS désactivée - non utilisée pour l'instant */}
                  {/* <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {room.lsStatus ? (
                    <span style={getLsStatusStyle(room.lsStatus)}>{room.lsStatus}</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td> */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    {room.luniButton && room.luniButton.lastHeartbeat ? (
                      <div className="flex flex-col items-start gap-1">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isHeartbeatRecent(room.luniButton.lastHeartbeat.lastHeartbeatDate) ? "bg-green-500" : "bg-red-500"}`} title={isHeartbeatRecent(room.luniButton.lastHeartbeat.lastHeartbeatDate) ? "Heartbeat récent (< 24h)" : "Heartbeat ancien (> 24h)"} />
                        <div className="text-xs text-gray-500">{formatHeartbeatDate(room.luniButton.lastHeartbeat.lastHeartbeatDate) || "—"}</div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-start gap-1">
                        <div className="w-3 h-3 rounded-full bg-gray-400 flex-shrink-0" title="Aucun bouton installé" />
                        <span className="text-xs text-gray-400">—</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {room.has_qr_code === 1 && room.last_stay_status ? (
                      <div className="space-y-2">
                        <div>
                          <span style={getStayStatusStyle(room.last_stay_status)}>{getStayStatusLabel(room.last_stay_status)}</span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          {room.last_stay_start_date && (
                            <div>
                              <span className="font-medium">Début:</span> {formatStayDate(room.last_stay_start_date)}
                            </div>
                          )}
                          <div>
                            <span className="font-medium">Fin:</span> {room.last_stay_end_date ? formatStayDate(room.last_stay_end_date) : "-"}
                          </div>
                          {!room.last_stay_start_date && !room.last_stay_end_date && room.last_stay_date && <div className="text-gray-500 italic">Créé le {formatQRDate(room.last_stay_date)}</div>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">{room.has_qr_code === 1 ? "Aucun séjour" : "—"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm relative">
                    <button type="button" onClick={() => setMenuRoomId(menuRoomId === room.rId ? null : room.rId)} className="text-gray-500 hover:text-gray-700 p-1" aria-label="Actions">
                      ⋮⋮⋮
                    </button>
                    {menuRoomId === room.rId && (
                      <div ref={menuRef} className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10 py-1">
                        {room.devEUI && app_id && (
                          <button type="button" onClick={() => openChirpstack(room)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                            Ouvrir Chirpstack
                          </button>
                        )}
                        {room.module_id && manager_id && (
                          <button
                            type="button"
                            onClick={() => {
                              navigate(`/hotels/${manager_id}/rooms/${room.manager_room_id}?tab=activity`);
                              setMenuRoomId(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Activité
                          </button>
                        )}
                        <button type="button" onClick={() => goEdit(room)} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          Modifier la chambre
                        </button>
                        <button type="button" onClick={() => goDelete(room)} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                          Supprimer la chambre
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500">{total_count > 0 ? `${rooms.length} chambre(s) affichée(s)` : "0 chambre"}</div>
      </div>

      {paginationBar}

      {/* Modal d'édition simplifiée */}
      {isEditModalOpen && selectedRoom && (
        <EditRoomModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          room={{ ...selectedRoom, room_nb: selectedRoom.room_nb ?? selectedRoom.rRoomNumber }}
          hotelId={manager_id}
          masterId={masterId}
          onRoomUpdated={handleRoomUpdated}
        />
      )}

      {/* Modal de confirmation de suppression (onglet Général : chambre seule + luni_data, module conservé) */}
      {isDeleteModalOpen && roomToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Voulez-vous vraiment supprimer la chambre <strong>{roomToDelete.rRoomNumber}</strong> ? Le module restera en base, non rattaché.
            </p>
            <div className="flex justify-end space-x-3">
              <button onClick={handleCloseDeleteModal} className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50">
                Annuler
              </button>
              <button onClick={handleConfirmDeleteRoom} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GlobalRoomsTable;
